import React from 'react';
import {View, StyleSheet, Text, PixelRatio} from 'react-native';
import DashedLine from 'react-native-dashed-line';
import Localized from 'src/constants/AppStrings';
import Styles from '../../Styles';
import AVTouchableOpacity from '../AVTouchableOpacity';
import AVText from '../AVText';
import {PreparationMethodValues} from 'src/types/PreparationMethods';

type PreparationMethodsProps = {
  onMethodSelected: (method: PreparationMethodValues) => void;
  selectedMethod: PreparationMethodValues;
  togoAvailable: boolean;
  dineInAvailable: boolean;
  lockerAvailable: boolean;
};
export default (props: PreparationMethodsProps) => {
  const prepMethod =
    props.selectedMethod === 'T'
      ? Localized.Labels.to_go
      : Localized.Labels.dine_in;
  const renderButton = (
    text: string,
    disabled: boolean,
    method: PreparationMethodValues,
  ) => {
    let updatedText = text;
    if (text === prepMethod) {
      updatedText += ' (selected)';
    }
    return (
      <AVTouchableOpacity
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={updatedText}
        accessibilityHint={`You have selected '${text}' as the preparation method for your order`}
        aria-label={updatedText}
        role="button"
        style={[styles.btnContainer, disabled && styles.disabledBtn]}
        onPress={() => {
          props.onMethodSelected(method);
        }}
      >
        <Text
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
          style={[styles.text, disabled && styles.disabledText]}
        >
          {text}
        </Text>
      </AVTouchableOpacity>
    );
  };

  let content;

  if (props.lockerAvailable) {
    const text = Localized.Labels.locker_only;
    content = (
      <View style={styles.container}>
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
          accessible={true}
          accessibilityLabel={Localized.Labels.locker_only}
          aria-label={Localized.Labels.locker_only}
          style={styles.methodText}
        >
          {text}
        </AVText>
      </View>
    );
  } else if (props.togoAvailable && props.dineInAvailable) {
    content = (
      <View style={styles.container}>
        {renderButton(
          Localized.Labels.to_go,
          props.selectedMethod !== PreparationMethodValues.ToGo,
          PreparationMethodValues.ToGo,
        )}
        {renderButton(
          Localized.Labels.dine_in,
          props.selectedMethod !== PreparationMethodValues.DineIn,
          PreparationMethodValues.DineIn,
        )}
      </View>
    );
  } else if (props.togoAvailable || props.dineInAvailable) {
    let text = Localized.Labels.to_go_only;

    if (props.dineInAvailable) {
      text = Localized.Labels.dine_in_only;
    }

    content = (
      <View style={styles.container}>
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          accessible={true}
          accessibilityLabel={text}
          aria-label={text}
          style={styles.methodText}
        >
          {text}
        </AVText>
      </View>
    );
  }

  if (content) {
    return (
      <View style={styles.preparationContainer}>
        <Text
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
          accessible={true}
          accessibilityLabel={Localized.Labels.preparation_method}
          aria-label={Localized.Labels.preparation_method}
          style={styles.preparationLabel}
        >
          {Localized.Labels.preparation_method}
        </Text>
        {content}
        <DashedLine
          dashGap={PixelRatio.roundToNearestPixel(4)}
          dashLength={PixelRatio.roundToNearestPixel(2)}
          dashThickness={1}
          dashColor="#999"
          style={[styles.dashStyle, styles.bottomDashedLine]}
        />
      </View>
    );
  }

  return null;
};
const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: Styles.Spacing.m2,
    backgroundColor: Styles.bgColor,
  },
  btnContainer: {
    flex: 1,
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
    height: Styles.Heights.touchTargetHeight1,
    paddingVertical: Styles.Spacing.m2,
    marginHorizontal: Styles.Spacing.m1,
    backgroundColor: Styles.primaryColor,
    borderRadius: PixelRatio.roundToNearestPixel(Styles.Spacing.m3),
  },
  disabledBtn: {
    backgroundColor: Styles.white,
    borderWidth: PixelRatio.roundToNearestPixel(1),
    borderColor: Styles.darkColor,
  },
  text: {
    color: Styles.white,
    fontWeight: 'bold',
  },
  disabledText: {
    color: Styles.darkColor,
  },
  methodText: {
    fontSize: Styles.Fonts.f1,
  },
  dashStyle: {
    height: PixelRatio.roundToNearestPixel(2),
    overflow: 'hidden',
    width: '100%',
  },
  bottomDashedLine: {
    marginTop: Styles.Spacing.m2,
  },
  preparationContainer: {
    alignSelf: 'stretch',
    marginHorizontal: Styles.Spacing.m3,
  },
  preparationLabel: {
    fontSize: Styles.Fonts.f0,
    fontWeight: '600',
    color: Styles.darkColor,
  },
});
